(() => {
  'use strict';

  const responseCache = new Map();
  const cacheLifetime = 60 * 1000;

  const normalizeSku = (value) => String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

  class RedsStoreAvailability extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.details = this.querySelector('details');
      this.status = this.querySelector('[data-availability-status]');
      this.list = this.querySelector('[data-availability-list]');
      this.productSection = this.closest('.product-section');
      this.sku = normalizeSku(this.dataset.sku);
      this.requestController = null;

      this.handleToggle = this.handleToggle.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);

      this.details?.addEventListener('toggle', this.handleToggle);
      this.productSection?.addEventListener('variantChange', this.handleVariantChange);

      if (this.details?.open) this.load();
    }

    disconnectedCallback() {
      this.details?.removeEventListener('toggle', this.handleToggle);
      this.productSection?.removeEventListener('variantChange', this.handleVariantChange);
      this.requestController?.abort();
      this.initialized = false;
    }

    handleToggle() {
      if (this.details?.open) this.load();
    }

    handleVariantChange(event) {
      const nextSku = normalizeSku(event.detail?.variant?.sku);
      if (nextSku === this.sku) return;

      this.sku = nextSku;
      this.dataset.sku = nextSku;
      this.requestController?.abort();
      this.requestController = null;
      this.clearList();
      this.showError();

      if (this.details?.open) this.load();
    }

    async load() {
      if (!this.sku) {
        this.showError();
        return;
      }

      const requestedSku = this.sku;
      this.showLoading();
      this.requestController?.abort();
      this.requestController = new AbortController();

      try {
        const data = await this.fetchAvailability(requestedSku, this.requestController.signal);
        if (requestedSku !== this.sku) return;
        if (data.stale === true || normalizeSku(data.sku) !== requestedSku) {
          throw new Error('Stale or mismatched store availability response.');
        }
        this.renderLocations(Array.isArray(data.locations) ? data.locations : []);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.showError();
      }
    }

    async fetchAvailability(sku, signal) {
      const endpoint = new URL(this.dataset.endpoint, window.location.origin);
      endpoint.searchParams.set('sku', sku);
      const cacheKey = endpoint.toString();
      const cached = responseCache.get(cacheKey);
      if (cached && Date.now() - cached.savedAt < cacheLifetime) {
        return cached.data;
      }

      const response = await fetch(cacheKey, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) throw new Error(`Availability request failed: ${response.status}`);

      const data = await response.json();
      responseCache.set(cacheKey, { data, savedAt: Date.now() });
      return data;
    }

    renderLocations(locations) {
      const showUnavailable = this.dataset.showUnavailable !== 'false';
      const safeLocations = locations
        .map((location) => ({
          code: String(location?.code || ''),
          name: String(location?.name || '').trim(),
          available: Math.max(0, Math.floor(Number(location?.available) || 0)),
        }))
        .filter((location) => location.code && location.name)
        .filter((location) => showUnavailable || location.available > 0);

      this.clearList();
      if (!safeLocations.length) {
        this.showStatus(this.dataset.unavailableLabel || 'Out of stock');
        return;
      }

      const numberFormatter = new Intl.NumberFormat(document.documentElement.lang || undefined);
      const inStockTemplate = this.dataset.inStockTemplate || 'In stock: __COUNT__';
      const unavailableLabel = this.dataset.unavailableLabel || 'Out of stock';

      safeLocations.forEach((location) => {
        const item = document.createElement('li');
        item.className = 'reds-store-availability__item';
        item.dataset.locationCode = location.code;

        const name = document.createElement('span');
        name.className = 'reds-store-availability__name';
        name.textContent = location.name;

        const availability = document.createElement('span');
        availability.className = 'reds-store-availability__quantity';
        availability.dataset.inStock = location.available > 0 ? 'true' : 'false';
        availability.textContent = location.available > 0
          ? inStockTemplate.replace('__COUNT__', numberFormatter.format(location.available))
          : unavailableLabel;

        item.append(name, availability);
        this.list.append(item);
      });

      this.status.hidden = true;
      this.list.hidden = false;
    }

    showLoading() {
      this.clearList();
      this.showStatus(this.dataset.loadingLabel || 'Checking availability…', true);
    }

    showError() {
      this.clearList();
      this.showStatus(this.dataset.errorLabel || 'Store availability is temporarily unavailable.');
    }

    showStatus(message, loading = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = false;
      this.status.dataset.loading = loading ? 'true' : 'false';
      if (this.list) this.list.hidden = true;
    }

    clearList() {
      if (this.list) this.list.replaceChildren();
    }
  }

  if (!customElements.get('reds-store-availability')) {
    customElements.define('reds-store-availability', RedsStoreAvailability);
  }
})();
